import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { DollarSign, TrendingUp, TrendingDown, PiggyBank, Plus, ArrowUpRight, ArrowDownRight, Calendar, Download, BarChart3, Pencil, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

const expenseCategories = [
  'aluguel', 'fornecedor', 'marketing', 'funcionarios', 'energia', 'agua', 'internet', 'outros'
];

const Financial = () => {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day');
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteEntry, setDeleteEntry] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [entryForm, setEntryForm] = useState({
    type: 'expense',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, [period, useCustomDate, dateFilter.startDate, dateFilter.endDate]);

  const fetchData = async () => {
    try {
      let summaryUrl = `${API}/financial/summary?period=${period}`;
      let entriesUrl = `${API}/financial/entries`;
      
      // Add date filters if custom dates are set
      if (useCustomDate && dateFilter.startDate && dateFilter.endDate) {
        summaryUrl = `${API}/financial/summary?start_date=${dateFilter.startDate}&end_date=${dateFilter.endDate}`;
        entriesUrl = `${API}/financial/entries?date_from=${dateFilter.startDate}&date_to=${dateFilter.endDate}`;
      }
      
      const [summaryRes, entriesRes, topRes] = await Promise.all([
        axios.get(summaryUrl),
        axios.get(entriesUrl),
        axios.get(`${API}/financial/top-products?limit=5`)
      ]);
      setSummary(summaryRes.data);
      setEntries(entriesRes.data);
      setTopProducts(topRes.data);
    } catch (e) {
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setUseCustomDate(false);
    setPeriod(newPeriod);
  };

  const handleDateFilterChange = (field, value) => {
    setDateFilter(prev => ({ ...prev, [field]: value }));
    if (field === 'endDate' && value && dateFilter.startDate) {
      setUseCustomDate(true);
    }
  };

  const clearDateFilter = () => {
    setDateFilter({ startDate: '', endDate: '' });
    setUseCustomDate(false);
  };

  const handleExportReport = async () => {
    setExporting(true);
    try {
      let exportUrl = `${API}/financial/export?period=${period}`;
      if (useCustomDate && dateFilter.startDate && dateFilter.endDate) {
        exportUrl = `${API}/financial/export?start_date=${dateFilter.startDate}&end_date=${dateFilter.endDate}`;
      }
      
      const response = await axios.get(exportUrl, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = useCustomDate 
        ? `relatorio_financeiro_${dateFilter.startDate}_${dateFilter.endDate}.csv`
        : `relatorio_financeiro_${period}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Relatório exportado!');
    } catch (e) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  const handleSaveEntry = async () => {
    if (!entryForm.amount || !entryForm.category) {
      toast.error('Preencha todos os campos');
      return;
    }

    try {
      if (editingEntry) {
        // Update existing entry
        await axios.put(`${API}/financial/entries/${editingEntry.id}`, {
          ...entryForm,
          amount: parseFloat(entryForm.amount)
        });
        toast.success('Lançamento atualizado');
      } else {
        // Create new entry
        await axios.post(`${API}/financial/entries`, {
          ...entryForm,
          amount: parseFloat(entryForm.amount)
        });
        toast.success('Lançamento registrado');
      }
      
      setShowEntryModal(false);
      setEditingEntry(null);
      setEntryForm({
        type: 'expense',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (e) {
      toast.error(editingEntry ? 'Erro ao atualizar lançamento' : 'Erro ao registrar lançamento');
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setEntryForm({
      type: entry.type,
      category: entry.category,
      amount: entry.amount.toString(),
      description: entry.description || '',
      date: entry.date.split('T')[0]
    });
    setShowEntryModal(true);
  };

  const handleDeleteEntry = async () => {
    if (!deleteEntry) return;
    
    try {
      await axios.delete(`${API}/financial/entries/${deleteEntry.id}`);
      toast.success('Lançamento excluído');
      setDeleteEntry(null);
      fetchData();
    } catch (e) {
      if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error('Erro ao excluir lançamento');
      }
    }
  };

  const handleCloseModal = () => {
    setShowEntryModal(false);
    setEditingEntry(null);
    setEntryForm({
      type: 'expense',
      category: '',
      amount: '',
      description: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  if (loading) {
    return (
      <DashboardLayout title="Financeiro">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Financeiro">
      <div className="space-y-6 pb-20 lg:pb-0">
        {/* Period Selector */}
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <div className="flex flex-wrap items-center gap-4">
            <Tabs value={useCustomDate ? '' : period} onValueChange={handlePeriodChange}>
              <TabsList className="bg-[#0f0f15]">
                <TabsTrigger value="day" className="data-[state=active]:bg-[#ff6b00]">Hoje</TabsTrigger>
                <TabsTrigger value="week" className="data-[state=active]:bg-[#ff6b00]">Semana</TabsTrigger>
                <TabsTrigger value="month" className="data-[state=active]:bg-[#ff6b00]">Mês</TabsTrigger>
              </TabsList>
            </Tabs>
            
            {/* Custom Date Filter */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-[#0f0f15] rounded-lg p-1">
                <Calendar className="w-4 h-4 text-white/40 ml-2" />
                <Input
                  type="date"
                  value={dateFilter.startDate}
                  onChange={(e) => handleDateFilterChange('startDate', e.target.value)}
                  className="w-32 bg-transparent border-0 text-white text-sm h-8 focus:ring-0"
                  placeholder="Data inicial"
                />
                <span className="text-white/40">-</span>
                <Input
                  type="date"
                  value={dateFilter.endDate}
                  onChange={(e) => handleDateFilterChange('endDate', e.target.value)}
                  className="w-32 bg-transparent border-0 text-white text-sm h-8 focus:ring-0"
                  placeholder="Data final"
                />
                {useCustomDate && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={clearDateFilter}
                    className="h-6 px-2 text-white/60 hover:text-white"
                  >
                    ✕
                  </Button>
                )}
              </div>
            </div>
            
            {useCustomDate && (
              <Badge className="bg-[#ff6b00]/20 text-[#ff6b00]">
                Filtro personalizado
              </Badge>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={handleExportReport}
              disabled={exporting}
              className="bg-white/10"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exportando...' : 'Exportar CSV'}
            </Button>
            <Button
              onClick={() => setShowEntryModal(true)}
              className="bg-[#ff6b00] hover:bg-[#ff8533]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Lançamento
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-adegga">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Receitas</p>
                  <p className="text-2xl font-bold text-green-400">
                    R$ {(summary?.income?.total || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-adegga">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Despesas</p>
                  <p className="text-2xl font-bold text-red-400">
                    R$ {(summary?.expenses?.total || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-adegga">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Lucro</p>
                  <p className={`text-2xl font-bold ${(summary?.profit || 0) >= 0 ? 'text-[#ff6b00]' : 'text-red-400'}`}>
                    R$ {(summary?.profit || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-[#ff6b00]/20 flex items-center justify-center">
                  <PiggyBank className="w-5 h-5 text-[#ff6b00]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-adegga">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-white/60">Pedidos</p>
                  <p className="text-2xl font-bold text-white">{summary?.orders?.count || 0}</p>
                  <p className="text-xs text-white/40">
                    Ticket: R$ {(summary?.orders?.ticket_medio || 0).toFixed(2)}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Entries List */}
          <Card className="card-adegga lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-white">Lançamentos</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10">
                    <TableHead className="text-white/60">Data</TableHead>
                    <TableHead className="text-white/60">Descrição</TableHead>
                    <TableHead className="text-white/60">Categoria</TableHead>
                    <TableHead className="text-white/60 text-right">Valor</TableHead>
                    <TableHead className="text-white/60 text-right w-24">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.slice(0, 20).map(entry => (
                    <TableRow key={entry.id} className="border-white/5 group">
                      <TableCell className="text-white/60">
                        {new Date(entry.date).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-white">
                        {entry.description || '-'}
                        {entry.source === 'order' && (
                          <Badge variant="outline" className="ml-2 text-xs border-blue-500/30 text-blue-400">
                            Pedido
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="bg-white/10 text-white/60">
                          {entry.category}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-bold ${
                        entry.type === 'income' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {entry.type === 'income' ? '+' : '-'} R$ {entry.amount.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-white/60 hover:text-white hover:bg-white/10"
                            onClick={() => handleEditEntry(entry)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            onClick={() => setDeleteEntry(entry)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {entries.length === 0 && (
                <div className="text-center py-8 text-white/40">
                  Nenhum lançamento registrado
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Products */}
          <Card className="card-adegga">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base">Produtos Mais Vendidos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {topProducts.map((prod, i) => (
                <div key={prod._id} className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#ff6b00]/20 flex items-center justify-center text-[#ff6b00] font-bold text-sm">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{prod.product_name}</p>
                    <p className="text-xs text-white/40">{prod.quantity} vendidos</p>
                  </div>
                  <span className="text-[#ff6b00] font-bold text-sm">
                    R$ {prod.revenue?.toFixed(2) || '0.00'}
                  </span>
                </div>
              ))}
              {topProducts.length === 0 && (
                <p className="text-center text-white/40 py-4">Sem dados de vendas</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Revenue by Category */}
        {summary?.income?.by_category && Object.keys(summary.income.by_category).length > 0 && (
          <Card className="card-adegga">
            <CardHeader className="pb-2">
              <CardTitle className="text-white">Receitas por Forma de Pagamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {Object.entries(summary.income.by_category).map(([cat, value]) => (
                  <div key={cat} className="bg-[#0f0f15] p-4 rounded-lg">
                    <p className="text-sm text-white/60 capitalize">{cat.replace('pedido_', '')}</p>
                    <p className="text-xl font-bold text-green-400">R$ {value.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Entry Modal */}
        <Dialog open={showEntryModal} onOpenChange={handleCloseModal}>
          <DialogContent className="bg-[#13131f] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingEntry ? 'Editar Lançamento' : 'Novo Lançamento'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Select
                  value={entryForm.type}
                  onValueChange={(v) => setEntryForm({ ...entryForm, type: v, category: '' })}
                >
                  <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10">
                    <SelectItem value="income">Receita</SelectItem>
                    <SelectItem value="expense">Despesa</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={entryForm.category}
                  onValueChange={(v) => setEntryForm({ ...entryForm, category: v })}
                >
                  <SelectTrigger className="bg-[#0f0f15] border-white/10 text-white">
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f15] border-white/10">
                    {entryForm.type === 'expense' ? (
                      expenseCategories.map(cat => (
                        <SelectItem key={cat} value={cat} className="capitalize">{cat}</SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="outros">Outros</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <Input
                type="number"
                step="0.01"
                placeholder="Valor (R$)"
                value={entryForm.amount}
                onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />

              <Input
                placeholder="Descrição"
                value={entryForm.description}
                onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />

              <Input
                type="date"
                value={entryForm.date}
                onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
                className="bg-[#0f0f15] border-white/10 text-white"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={handleCloseModal}>Cancelar</Button>
              <Button className="bg-[#ff6b00] hover:bg-[#ff8533]" onClick={handleSaveEntry}>
                {editingEntry ? 'Atualizar' : 'Salvar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteEntry} onOpenChange={() => setDeleteEntry(null)}>
          <AlertDialogContent className="bg-[#13131f] border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Excluir Lançamento</AlertDialogTitle>
              <AlertDialogDescription className="text-white/60">
                Tem certeza que deseja excluir este lançamento?
                {deleteEntry && (
                  <div className="mt-2 p-3 bg-[#0f0f15] rounded-lg">
                    <p className="text-white">{deleteEntry.description || deleteEntry.category}</p>
                    <p className={`font-bold ${deleteEntry.type === 'income' ? 'text-green-400' : 'text-red-400'}`}>
                      {deleteEntry.type === 'income' ? '+' : '-'} R$ {deleteEntry.amount?.toFixed(2)}
                    </p>
                  </div>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/10 text-white border-white/10 hover:bg-white/20">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction 
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={handleDeleteEntry}
              >
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default Financial;
